import moment from "moment";
import reportService from "@/services/report";

export const MONTH = "YYYY-MM";
export const ISO = "YYYY-MM-DD";

/**
 * Narudžbe kreditnog korisnika firme "više restorana" po mesecima (telefon i računar): mesec kreće od kraja
 * izabranog perioda, narudžbe sa iznosom i potrošenim kreditom stižu iz /credits/report. Ekran mora imati
 * `user` ({ user_id, user_name }) i `companyId` (prop ili computed).
 */
export default {
  data() {
    const to = this.$store.state.report.filters.dates.to;
    return {
      month: moment(to, ISO).isValid() ? moment(to, ISO).format(MONTH) : moment().format(MONTH),
      report: null,
      loading: false,
      failed: false,
      requestId: 0,
    };
  },
  computed: {
    monthStart() {
      return moment(this.month, MONTH).startOf("month");
    },
    formatLocale() {
      const config = this.getCurrentLanguageConfig();
      return config.script === "latin" && config.locale === "sr-RS" ? "sr-Latn-RS" : config.locale;
    },
    monthTitle() {
      const name = new Intl.DateTimeFormat(this.formatLocale, { month: "long" }).format(this.monthStart.toDate());
      return `${name.charAt(0).toUpperCase()}${name.slice(1)} ${this.monthStart.year()}`;
    },
    monthHint() {
      const offset = this.monthStart.diff(moment().startOf("month"), "months");
      if (offset === 0) {
        return this.$t("report_mobile.month_current");
      }
      return offset === -1 ? this.$t("report_mobile.presets.last_month") : "";
    },
    currency() {
      return this.majorityRestaurantCurrency || "";
    },
    orders() {
      const weekday = new Intl.DateTimeFormat(this.formatLocale, { weekday: "short" });
      return ((this.report && this.report.orders) || [])
        .filter((order) => order && order.id)
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .map((order) => {
          const date = moment(order.date);
          const total = (order.total_cents || 0) / 100;
          const credits = Number(order.credits_spent) || 0;
          return {
            id: order.id,
            credits,
            day: date.isValid() ? `${date.date()}.` : "",
            weekday: date.isValid() ? weekday.format(date.toDate()).replace(/\.$/, "") : "",
            items: this.toLatin((order.items || []).map((item) => item.name).join(", ")),
            meta: [this.toLatin(order.restaurant || ""), `${this.$t("amount")} ${this.money(total)}`, `${this.$t("spent_on_credits")} ${this.money(Math.abs(credits))}`]
              .filter(Boolean)
              .join(" · "),
          };
        });
    },
    creditsTotal() {
      return Math.abs(this.orders.reduce((sum, order) => sum + order.credits, 0));
    },
  },
  watch: {
    user: {
      handler() {
        this.load();
      },
      immediate: true,
    },
    month() {
      this.load();
    },
  },
  methods: {
    money(value) {
      return `${(Number(value) || 0).toFixed(2)}${this.currency ? ` ${this.currency}` : ""}`;
    },
    stepMonth(step) {
      this.month = this.monthStart.clone().add(step, "month").format(MONTH);
    },
    async load() {
      if (!this.user || !this.companyId) {
        return;
      }
      const requestId = ++this.requestId;
      this.loading = true;
      this.failed = false;
      try {
        const { data } = await reportService.getCreditUser({
          date_from: this.monthStart.format(ISO),
          date_to: this.monthStart.clone().endOf("month").format(ISO),
          company_id: this.companyId,
          user_id: this.user.user_id,
        });
        if (requestId !== this.requestId) {
          return;
        }
        const list = Array.isArray(data) ? data : data && data.users;
        this.report = Array.isArray(list)
          ? list.find((item) => item.user_id === this.user.user_id) || list[0] || null
          : data || null;
      } catch (error) {
        if (requestId === this.requestId) {
          this.report = null;
          this.failed = true;
        }
      } finally {
        if (requestId === this.requestId) {
          this.loading = false;
        }
      }
    },
  },
};
